"use client";

import { Children, isValidElement, type ButtonHTMLAttributes, type ReactNode } from "react";
import "./studio-chrome-kit.css";

export type StudioButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "danger"
  /** @deprecated use secondary */
  | "default"
  /** @deprecated use secondary */
  | "muted";

export type StudioButtonSize = "default" | "compact" | "fit";

function buttonVariantClass(variant: StudioButtonVariant): string {
  if (variant === "default" || variant === "muted" || variant === "secondary") {
    return "is-secondary";
  }
  return `is-${variant}`;
}

type StudioButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: StudioButtonVariant;
  size?: StudioButtonSize;
  children?: ReactNode;
};

/**
 * Locked chrome action button — wraps `.cursor-settings-action`.
 * Prefer this over inventing `*-btn` / one-off primary fills.
 */
/** No text at all, just icon element(s): Studio draws these as circles. */
function iconOnly(children: ReactNode): boolean {
  const parts = Children.toArray(children);
  if (!parts.length) return false;
  return parts.every((part) => {
    if (typeof part === "string" || typeof part === "number") return false;
    if (!isValidElement(part)) return true;
    // A label span (or any element with text children) is not an icon.
    const props = part.props as { children?: ReactNode };
    return typeof part.type !== "string" || part.type === "svg" || props.children == null;
  });
}

export function StudioButton({
  variant = "secondary",
  size = "default",
  className = "",
  type = "button",
  children,
  ...rest
}: StudioButtonProps) {
  const cls = [
    "cursor-settings-action",
    "studio-chrome-btn",
    buttonVariantClass(variant),
    size === "compact" ? "is-compact" : "",
    size === "fit" ? "is-fit" : "",
    iconOnly(children) ? "is-icon-only" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button type={type} className={cls} {...rest}>
      {children}
    </button>
  );
}
